import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { httpClient } from '@/utils/http/client'
import { setLocale as setI18nLocale } from '@/plugins/i18n'
import {
  type Theme,
  type SupportedLocale,
  type UserSettings,
  getStoredTheme,
  setStoredTheme,
  getStoredLocale,
  setStoredLocale,
  DEFAULT_SETTINGS,
} from '@/types/settings'

/**
 * Settings store for managing user preferences (theme and locale).
 *
 * This store handles:
 * - Cookie persistence for cross-subdomain consistency
 * - API synchronization for authenticated users
 * - Graceful fallbacks when API is unavailable
 */
export const useSettingsStore = defineStore('settings', () => {
  // State
  const theme = ref<Theme>(DEFAULT_SETTINGS.theme)
  const locale = ref<SupportedLocale>(DEFAULT_SETTINGS.locale)
  const loading = ref(false)
  const initialized = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const settings = computed<UserSettings>(() => ({
    theme: theme.value,
    locale: locale.value,
  }))

  /**
   * Get system theme preference
   */
  function getSystemTheme(): 'light' | 'dark' {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  /**
   * Apply theme to document (add/remove 'dark' class)
   * This ensures the dark class is applied immediately on load
   */
  function applyThemeToDocument(resolvedThemeValue: 'light' | 'dark'): void {
    if (typeof document === 'undefined') return
    if (resolvedThemeValue === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Resolve theme to actual value (light or dark)
   */
  const resolvedTheme = computed<'light' | 'dark'>(() => {
    if (theme.value === 'system') {
      return getSystemTheme()
    }
    return theme.value
  })

  /**
   * Read settings from URL parameters.
   * Used for cross-subdomain sharing in development (localhost).
   * Returns any valid settings found and cleans the URL for better UX.
   */
  function readAndClearUrlParams(): { theme?: Theme; locale?: SupportedLocale } {
    if (typeof window === 'undefined') return {}

    const urlParams = new URLSearchParams(window.location.search)
    const result: { theme?: Theme; locale?: SupportedLocale } = {}

    const urlTheme = urlParams.get('theme')
    if (urlTheme && ['light', 'dark', 'system'].includes(urlTheme)) {
      result.theme = urlTheme as Theme
    }

    const urlLocale = urlParams.get('locale')
    if (urlLocale && ['en', 'pl'].includes(urlLocale)) {
      result.locale = urlLocale as SupportedLocale
    }

    // Clean URL if settings params were present (for cleaner UX)
    if (urlTheme || urlLocale) {
      urlParams.delete('theme')
      urlParams.delete('locale')
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '') +
        window.location.hash
      window.history.replaceState({}, '', newUrl)
    }

    return result
  }

  /**
   * Initialize settings from URL params or cookies/localStorage.
   * Called on app startup - applies theme immediately to prevent FOUC.
   *
   * Priority order:
   * 1. URL parameters (for cross-subdomain navigation in development)
   * 2. Cookies (cross-subdomain in production)
   * 3. localStorage (fallback for localhost)
   */
  function initializeFromStorage(): void {
    // First, check URL params (for cross-subdomain navigation)
    // This is needed because cookies don't share between localhost subdomains
    const urlSettings = readAndClearUrlParams()

    // Apply theme: URL params take priority, then fall back to storage
    if (urlSettings.theme) {
      theme.value = urlSettings.theme
      setStoredTheme(urlSettings.theme)
    } else {
      const storedTheme = getStoredTheme()
      if (storedTheme) {
        theme.value = storedTheme
      }
    }

    // Apply locale: URL params take priority, then fall back to storage
    if (urlSettings.locale) {
      locale.value = urlSettings.locale
      setStoredLocale(urlSettings.locale)
      // Sync with i18n plugin (fire and forget since messages are pre-loaded)
      void setI18nLocale(urlSettings.locale)
    } else {
      const storedLocale = getStoredLocale()
      if (storedLocale) {
        locale.value = storedLocale
        // Sync with i18n plugin (fire and forget since messages are pre-loaded)
        void setI18nLocale(storedLocale)
      }
    }

    // Apply theme to document immediately to prevent flash of wrong theme
    const resolved = theme.value === 'system' ? getSystemTheme() : theme.value
    applyThemeToDocument(resolved)

    initialized.value = true
  }

  /**
   * Apply settings from API response (on login)
   * API settings override cookies
   */
  function applyFromApi(apiSettings: UserSettings): void {
    if (apiSettings.theme) {
      theme.value = apiSettings.theme
      setStoredTheme(apiSettings.theme)
      // Apply theme to document
      const resolved = apiSettings.theme === 'system' ? getSystemTheme() : apiSettings.theme
      applyThemeToDocument(resolved)
    }

    if (apiSettings.locale) {
      locale.value = apiSettings.locale
      setStoredLocale(apiSettings.locale)
      // Sync with i18n plugin (fire and forget since messages are pre-loaded)
      void setI18nLocale(apiSettings.locale)
    }
  }

  /**
   * Set theme preference
   * Updates cookie, applies to document, and optionally syncs to API
   */
  async function setTheme(newTheme: Theme, syncToApi = true): Promise<void> {
    theme.value = newTheme
    setStoredTheme(newTheme)

    // Apply theme to document
    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme
    applyThemeToDocument(resolved)

    if (syncToApi) {
      await syncSettingsToApi({ theme: newTheme })
    }
  }

  /**
   * Set locale preference
   * Updates cookie, syncs with i18n, and optionally syncs to API
   */
  async function setLocale(newLocale: SupportedLocale, syncToApi = true): Promise<void> {
    locale.value = newLocale
    setStoredLocale(newLocale)

    // Sync with i18n plugin
    await setI18nLocale(newLocale)

    if (syncToApi) {
      await syncSettingsToApi({ locale: newLocale })
    }
  }

  /**
   * Sync current settings to API
   * Only called when user is authenticated
   */
  async function syncSettingsToApi(updates: Partial<UserSettings>): Promise<void> {
    loading.value = true
    error.value = null

    try {
      await httpClient.patch('/users/me/settings', updates)
    } catch (err) {
      // Log error but don't throw - localStorage already updated
      console.warn('[Settings] Failed to sync to API:', err)
      error.value = 'Failed to save settings to server'
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch settings from API
   * Called to sync after login if user data doesn't include settings
   */
  async function fetchSettingsFromApi(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.get<UserSettings>('/users/me/settings')
      applyFromApi(response.data)
    } catch (err) {
      console.warn('[Settings] Failed to fetch from API:', err)
      error.value = 'Failed to load settings from server'
    } finally {
      loading.value = false
    }
  }

  /**
   * Reset settings to defaults
   * Called on logout (but keeps cookies for landing page)
   */
  function reset(): void {
    // Note: We intentionally DO NOT clear cookies on logout
    // This allows the landing page to retain user's theme preference
    theme.value = getStoredTheme() || DEFAULT_SETTINGS.theme
    locale.value = getStoredLocale() || DEFAULT_SETTINGS.locale
    error.value = null
  }

  return {
    // State
    theme: computed(() => theme.value),
    locale: computed(() => locale.value),
    loading: computed(() => loading.value),
    initialized: computed(() => initialized.value),
    error: computed(() => error.value),

    // Getters
    settings,
    resolvedTheme,

    // Actions
    initializeFromStorage,
    applyFromApi,
    setTheme,
    setLocale,
    fetchSettingsFromApi,
    reset,
  }
})
